// src/store/slices/socketSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

// Define types for the socket state
interface SocketState {
  connected: boolean;
  currentRoom: string | null;
  username: string | null;
  error: string | null;
  isJoining: boolean;
  isLeaving: boolean;
}

// Initial state
const initialState: SocketState = {
  connected: false,
  currentRoom: null,
  username: null,
  error: null,
  isJoining: false,
  isLeaving: false
};

// Create the socket slice
const socketSlice = createSlice({
  name: 'socket',
  initialState,
  reducers: {
    // Connection status actions
    socketConnected: (state) => {
      state.connected = true;
      state.error = null;
    },
    socketDisconnected: (state) => {
      state.connected = false;
    },
    socketError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearSocketError: (state) => {
      state.error = null;
    },
    
    // Room actions
    joinRoomRequest: (state, action: PayloadAction<{username: string, room: string}>) => {
      state.isJoining = true;
      state.username = action.payload.username;
    },
    joinRoomSuccess: (state, action: PayloadAction<string>) => {
      state.currentRoom = action.payload;
      state.isJoining = false;
      state.error = null;
    },
    joinRoomFailure: (state, action: PayloadAction<string>) => {
      state.isJoining = false;
      state.error = action.payload;
    },
    leaveRoomRequest: (state) => {
      state.isLeaving = true;
    },
    leaveRoomSuccess: (state) => {
      state.currentRoom = null;
      state.isLeaving = false;
    },
    leaveRoomFailure: (state, action: PayloadAction<string>) => {
      state.isLeaving = false;
      state.error = action.payload;
    }
  }
});

// Export actions
export const {
  socketConnected,
  socketDisconnected,
  socketError,
  clearSocketError,
  joinRoomRequest,
  joinRoomSuccess,
  joinRoomFailure,
  leaveRoomRequest,
  leaveRoomSuccess,
  leaveRoomFailure
} = socketSlice.actions;

// Export selectors
export const selectSocketConnected = (state: RootState) => state.whatsapp.socket.connected;
export const selectCurrentRoom = (state: RootState) => state.whatsapp.socket.currentRoom;
export const selectSocketError = (state: RootState) => state.whatsapp.socket.error;
export const selectIsJoiningRoom = (state: RootState) => state.whatsapp.socket.isJoining;
export const selectIsLeavingRoom = (state: RootState) => state.whatsapp.socket.isLeaving;

// Export reducer
export default socketSlice.reducer;